import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { PieChart } from 'react-minimal-pie-chart'

import { format, isValid, parseISO } from 'date-fns'

import { useState } from 'react'

import SVGImage from '~/components/Image/SVGImage'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'
import useTestPrepProductCardStats from '~/statistics/hooks/useTestPrepProductCardStats'
import { ButtonPrimary } from '~/components/Buttons'
import { TestPrepProductAPIStat } from '~/redux/apis/user-stats-api'
import ROUTES from '~/shared/constants/routes.const'
import useRetakeExam from '~/tests/hooks/useRetakeExam'
import ScoreboardTestPrepChart from '~/shared/components/Scoreboard/ScoreboardTestPrepChart'

interface Props {
  product: TestPrepProductAPIStat
}

export const formatDate = (input: string | number | Date | undefined | null): string => {
  if (!input) return ''
  let date: Date

  // Handle YYYY-MM-DD explicitly to avoid timezone shifts
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    date = parseISO(input) // safe, no timezone conversion
  } else {
    date = new Date(input)
  }

  if (!isValid(date)) return ''

  // 'do' -> day with ordinal, 'MMMM' -> full month, 'yyyy' -> year
  return format(date, 'do MMMM yyyy')
}

export default function Details({ product }: Props): React.ReactElement {
  const { t } = useTranslation('statistics')
  const router = useRouter()
  const isFamilyView = router.asPath.includes(ROUTES.FAMILY_ADMIN_PANEL)
  const handleRetakeExam = useRetakeExam()
  const [showBarChartModal, setShowBarChartModal] = useState(-1)

  const {
    scoreSummary: { totalQuestionsCount, answeredQuestionsCount, correctAnswersCount },
    testBuildUrl,
    levelsByDate,
  } = useTestPrepProductCardStats(product)

  const score = correctAnswersCount
  const totalScore = totalQuestionsCount
  const scorePercentage = totalQuestionsCount > 0 ? Math.floor((correctAnswersCount / totalQuestionsCount) * 100) : 0

  const onClickBuildTestBtn = (): void => {
    statisticsAnalytics.logButtonClick(t('goToBuildATest'))
  }
  const onRetakeBtnClick = function (examID: number): void {
    statisticsAnalytics.logTestprepRetakeButtonClick({ examID })
    handleRetakeExam(examID)
  }

  return (
    <div className="space-y-5">
      {/* Score Summary Card */}
      <div className="w-full font-semibold rounded overflow-hidden shadow-card space-y-3 py-4 px-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <p className="text-purple">{t('score')}</p>
            <p className="text-black">
              {score}/{totalScore}
            </p>
          </div>
        </div>
        <div className="relative h-24 flex items-center justify-center">
          <PieChart
            data={[
              { value: scorePercentage, color: '#7D38D6' },
              { value: 100 - scorePercentage, color: '#AFAFAF' },
            ]}
            lineWidth={28}
            radius={49}
            startAngle={270}
          />
        </div>
        <div className="flex gap-2 items-center justify-center">
          <ResponsiveImage
            alt={t('alt_texts.answered')}
            height={40}
            imageClassName="w-4.5"
            mediaQuery={[{ screen: '(min-width: 280px)', width: '60vw' }]}
            srcKey="scoreboard/activities-done.png"
            width={35}
          />
          <p>{t('answered', { n: answeredQuestionsCount })}</p>
        </div>
      </div>

      {/* Recent Test History */}
      <div className="space-y-4 max-h-[1586px] overflow-y-auto shadow-card p-2 rounded">
        {[...Object.entries(levelsByDate)].map(([date, levelStat], index, array) => (
          <div key={`${date}-${product.gradeTitle}-${product.curriculumCountryCode}`}>
            <h3 className="font-medium pb-2">{formatDate(date)}</h3>
            {levelStat.testSet.map((test) => (
              <div key={`${date}-${test.examId}`}>
                {showBarChartModal === test.testIncrementalId && (
                  <ScoreboardTestPrepChart
                    attempts={test.allScores}
                    setShowBarChartModal={setShowBarChartModal}
                    testNo={test.testIncrementalId}
                  />
                )}

                <div className="flex items-center justify-between py-2" key={`${date}-${test.examId}`}>
                  <p>{t('statistics:testWithNumber', { n: test.testIncrementalId })}</p>
                  <p>
                    {test.correctnessPercentage}% {t('correct')}
                  </p>
                  <div className="flex space-x-2">
                    <SVGImage
                      alt={t('alt_texts.chart')}
                      imageClassName="cursor-pointer"
                      onClick={() => setShowBarChartModal(test.testIncrementalId)}
                      srcKey="scoreboard/stat-icon.svg"
                    />
                    {!isFamilyView && (
                      <SVGImage
                        alt={t('alt_texts.retake')}
                        imageClassName="cursor-pointer"
                        onClick={() => {
                          onRetakeBtnClick(test.examId)
                        }}
                        srcKey="scoreboard/retake-icon.svg"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {index < array.length - 1 && <div className="bg-purple-light w-full my-4 h-0.5" />}
          </div>
        ))}
      </div>

      {/* Go to Build-a-test Button */}
      {!isFamilyView && (
        <div className="flex justify-end mt-4">
          <ButtonPrimary className="h-10 !px-2.5" href={testBuildUrl} onClick={onClickBuildTestBtn} size="medium">
            {t('goToBuildATest')}
          </ButtonPrimary>
        </div>
      )}
    </div>
  )
}
